import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { ApiError } from "@/lib/api";
import { AUTH_QUERY_KEY } from "@/hooks/useAuth";

/**
 * Sur toute erreur `401` remontée par une query/mutation protégée (session serveur expirée ou
 * invalidée), on remet la session locale à `null` : `ProtectedRoute` voit alors `user === null` et
 * redirige vers `/login`. Sans ça, un onglet resté ouvert après l'expiration côté serveur garderait
 * un `user` figé en cache et l'utilisateur resterait bloqué sur des erreurs (régression vs le
 * `onAuthStateChange` de Supabase). `getMe()` avale déjà le 401 en `null` → pas de boucle sur la
 * query d'auth elle-même.
 */
export function handleApiAuthError(error: unknown): void {
  if (error instanceof ApiError && error.status === 401) {
    queryClient.setQueryData(AUTH_QUERY_KEY, null);
  }
}

/**
 * Client TanStack Query partagé. Stale-while-revalidate par défaut, refresh
 * sur focus pour rester proche du temps réel sans surcharger le backend.
 */
export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: handleApiAuthError }),
  mutationCache: new MutationCache({ onError: handleApiAuthError }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
});
